/**
 * Shared UA-locale formatters. Keep every number/date rendering in the app
 * going through these so the whole dashboard reads consistently.
 */

// `currencyDisplay: "narrowSymbol"` is required for the documented "₴" output:
// Chrome's uk-UA ICU data renders the default symbol as "грн".
const UAH = new Intl.NumberFormat("uk-UA", {
  style: "currency",
  currency: "UAH",
  currencyDisplay: "narrowSymbol",
  maximumFractionDigits: 0,
});

const UAH_PRECISE = new Intl.NumberFormat("uk-UA", {
  style: "currency",
  currency: "UAH",
  currencyDisplay: "narrowSymbol",
  maximumFractionDigits: 2,
});

const NUM = new Intl.NumberFormat("uk-UA");

/** 12345.6 -> "12 346 ₴" */
export const money = (value: number): string => UAH.format(value);

/** 12345.67 -> "12 345,67 ₴" */
export const moneyPrecise = (value: number): string => UAH_PRECISE.format(value);

/** 12345 -> "12 345" */
export const num = (value: number): string => NUM.format(value);

/** 42.371 -> "42,4%" */
export const pct = (value: number, digits = 1): string =>
  `${value.toLocaleString("uk-UA", { maximumFractionDigits: digits })}%`;

/** 135 -> "2 год 15 хв" */
export function minutes(total: number): string {
  const h = Math.floor(total / 60);
  const m = Math.round(total % 60);
  if (h === 0) return `${m} хв`;
  return m === 0 ? `${h} год` : `${h} год ${m} хв`;
}

/** "2026-08-03" -> "3 серп." */
export function shortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("uk-UA", { day: "numeric", month: "short" });
}

/** "2026-08-14" -> "Пн".."Нд" (Monday-first Ukrainian week). */
export function weekdayShort(iso: string): string {
  const names = ["Нд", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];
  return names[new Date(`${iso}T00:00:00`).getDay()];
}

/** "2026-08" -> "Серпень 2026". */
export function monthTitle(yyyyMm: string): string {
  const d = new Date(`${yyyyMm}-01T00:00:00`);
  const name = d.toLocaleDateString("uk-UA", { month: "long" });
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${d.getFullYear()}`;
}

/** "2026-08-14" -> "14 серпня, пʼятниця". */
export function dayTitle(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  const dayMonth = d.toLocaleDateString("uk-UA", { day: "numeric", month: "long" });
  const weekday = d.toLocaleDateString("uk-UA", { weekday: "long" });
  return `${dayMonth}, ${weekday}`;
}

/** "2026-08-14" -> "14 серпня". */
export function dayLabel(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("uk-UA", {
    day: "numeric",
    month: "long",
  });
}

/** "2026-08-14" -> "Пʼятниця, 14 серпня 2026". */
export function fullDayLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  const weekday = d.toLocaleDateString("uk-UA", { weekday: "long" });
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${dayLabel(iso)} ${d.getFullYear()}`;
}

/**
 * ISO-8601 timestamp -> "14:20". Empty string for anything unparsable.
 *
 * The empty string matters: this feeds labels like «Збережено о 14:20», and a
 * literal "Invalid Date" in that slot reads as a broken save, not a broken date.
 */
export function clockTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit" });
}

/** Sign-aware delta label: 12.5 -> "+12,5%", -3 -> "−3%" */
export function delta(value: number | null, digits = 1): string {
  if (value === null || Number.isNaN(value)) return "—";
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  return `${sign}${Math.abs(value).toLocaleString("uk-UA", { maximumFractionDigits: digits })}%`;
}

/**
 * Ukrainian plural picker: plural(2, "день", "дні", "днів") -> "дні".
 * `one` for 1, 21, 31…; `few` for 2–4, 22–24…; `many` for 0, 5–20, 11–14.
 * Returns the word only — callers compose "N слово" themselves.
 */
export function plural(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(Math.trunc(n));
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/** 4231 -> "4,2 с" (uk-UA decimal comma, same as `num`/`pct`) */
export function seconds(ms: number): string {
  const value = (ms / 1000).toLocaleString("uk-UA", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return `${value} с`;
}

const DAY_MONTH_LONG = new Intl.DateTimeFormat("uk-UA", { day: "numeric", month: "long" });
const DAY_MONTH_SHORT = new Intl.DateTimeFormat("uk-UA", { day: "numeric", month: "short" });

/**
 * Honest label for a date range. Both ends are ISO `YYYY-MM-DD` Kyiv calendar
 * days, so they are parsed as local dates — no timezone maths anywhere.
 * Same month: "1–10 серпня". Across months: "28 лип. – 10 серп.".
 * Across years the year is appended to each end: "28 груд. 2025 – 10 січ. 2026".
 * The year is appended manually because uk-UA renders it as "2025 р.", which
 * reads as noise inside a compact range label.
 */
export function rangeLabel(fromIso: string, toIso: string): string {
  const from = new Date(`${fromIso}T00:00:00`);
  const to = new Date(`${toIso}T00:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return `${fromIso} – ${toIso}`;

  if (from.getFullYear() !== to.getFullYear()) {
    return `${DAY_MONTH_SHORT.format(from)} ${from.getFullYear()} – ${DAY_MONTH_SHORT.format(to)} ${to.getFullYear()}`;
  }
  if (from.getMonth() !== to.getMonth()) {
    return `${DAY_MONTH_SHORT.format(from)} – ${DAY_MONTH_SHORT.format(to)}`;
  }
  // A one-day range is a date, not a range: "10 серпня", never "10–10 серпня".
  if (from.getDate() === to.getDate()) return DAY_MONTH_LONG.format(to);
  return `${from.getDate()}–${DAY_MONTH_LONG.format(to)}`;
}

/** Telegram Stars, as the bot's own currency enum spells it. */
const STARS = new Set(["STARS", "XTR"]);

/**
 * An amount in whatever currency it was actually paid in.
 *
 * Hryvnia goes through `money` so it reads like every other hryvnia figure on
 * the page, and Stars get the star rather than the ticker: «300 ⭐» is a price
 * a person recognises, «300 STARS» is a database column. They are never added
 * together — Stars have no NBU rate, so a combined total would be invented.
 */
export function moneyOf(amount: number, currency: string): string {
  const code = currency.toUpperCase();
  if (code === "UAH") return money(amount);
  if (STARS.has(code)) return `${num(amount)} ⭐`;
  return moneyIn(amount, currency);
}

// Formatters are cached per currency+precision: building an Intl.NumberFormat
// is the expensive part, and account lists re-render on every poll.
const MONEY_IN_CACHE = new Map<string, (value: number) => string>();

/**
 * Balance in a non-UAH currency. Small balances keep two decimals: rounding
 * turned a real $0.09 card into a flat "0 USD" while the hryvnia line
 * underneath still said "≈ 4 ₴". Big ones round, because "1 234,00 $" is noise.
 *
 * `Intl.NumberFormat` only knows ISO-4217, so a crypto ticker — USDT, BTC, TON —
 * makes it throw `RangeError`, and an exception thrown while rendering a list
 * takes the entire page down with it. One crypto account used to blank the whole
 * transactions screen. A formatter that cannot format is allowed to be plain; it
 * is never allowed to crash.
 */
export function moneyIn(amount: number, currency: string): string {
  const digits = Math.abs(amount) >= 100 ? 0 : 2;
  const key = `${currency}:${digits}`;
  let format = MONEY_IN_CACHE.get(key);
  if (!format) {
    // The whole formatter is cached, not the Intl object: the fallback appends
    // the ticker itself, and caching only the Intl part would make the first
    // call and every later one disagree.
    try {
      const intl = new Intl.NumberFormat("uk-UA", {
        style: "currency",
        currency,
        maximumFractionDigits: digits,
      });
      format = (value: number) => intl.format(value);
    } catch {
      const intl = new Intl.NumberFormat("uk-UA", { maximumFractionDigits: digits });
      format = (value: number) => `${intl.format(value)} ${currency}`;
    }
    MONEY_IN_CACHE.set(key, format);
  }
  return format(amount);
}
